import { DocumentCitationStyleLabels, type DocumentConfig, DocumentLanguageLabels } from '@alpod-ai-writer/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Label, Select, Textarea, TextInput } from 'flowbite-react';
import { LifeBuoyIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { useModals } from '../../../hooks/useModals.hook';
import { pathWithParams } from '../../../lib/routing.utils';
import { sortedDocumentTypeKeys } from '../../../lib/template.utils';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { MissingTemplateModal } from '../../modals/feedback/missingTemplateModal.component';
import { RouteIndex } from '../../routeIndex';

interface FormValues {
    documentName: string;
    templateId: string;
    documentType: string;
    goal: string;
}

const NewDocumentView = () => {
    const navigate = useNavigate();
    const api = useApi();
    const modals = useModals();

    const { register, handleSubmit, formState: { errors }, watch } = useForm<FormValues>({
        defaultValues: {
            documentName: '',
            templateId: '',
            documentType: '',
            goal: ''
        }
    });

    const templateId = watch('templateId');

    const templateList = useQuery({
        queryKey: [ 'documentTemplateList' ],
        queryFn: () => api.v1.templates.getAll()
    });

    const templateDetail = useQuery({
        queryKey: [ 'documentTemplate', templateId ],
        enabled: !!templateId,
        queryFn: () => api.v1.templates.getById(templateId)
    });

    const saveNewDocument = useMutation({
        mutationFn: (data: FormValues) => api.v1.documents.create({ ...data }),
        onSuccess: (data) => {
            navigate(pathWithParams(
                RouteIndex.documentDetail.edit,
                {
                    documentId: data.documentId
                }
            ));
        }
    });

    const onSubmit = useCallback((data: FormValues) => {
        saveNewDocument.mutate(data);
    }, []);

    const handleMissingTemplate = useCallback(() => {
        modals.showModal('missing-template', MissingTemplateModal, {
            locationInApp: 'Nový dokument'
        });
    }, []);

    const documentType = watch('documentType');
    const templateConfig = templateDetail.data?.config as DocumentConfig|undefined;
    const templateTypeConfig = templateConfig?.types[documentType];

    return (
        <>
            <AppHeader
                pageTitle='Nový dokument'
                backLink={RouteIndex.documentList}
            />
            <div className="flex flex-col justify-start items-center min-h-screen pt-14">
                <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6 p-4 md:p-8 lg:py-12 w-[36rem] max-w-full'>
                    <div>
                        <Label htmlFor="documentName" className="block mb-1">Název dokumentu</Label>
                        <TextInput
                            id="documentName"
                            type="text"
                            sizing='lg'
                            placeholder="Bakalářská práce na téma..."
                            {...register('documentName', { required: 'Název dokumentu je povinný.' })}
                            color={errors.documentName ? 'failure' : 'default'}
                            helperText={(
                                <>
                                    {errors.documentName && (
                                        <span className='block'>
                                            {errors.documentName.message}
                                        </span>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    <div>
                        <Label htmlFor="templateId" className="block mb-1">Vyberte šablonu podle Vaší školy a fakulty</Label>
                        <Select
                            id="templateId"
                            {...register('templateId', { required: 'Šablona je povinná.' })}
                            color={errors.templateId ? 'failure' : 'default'}
                            helperText={(
                                <>
                                    {errors.templateId && (
                                        <span className='block'>
                                            {errors.templateId.message}
                                        </span>
                                    )}
                                </>
                            )}
                        >
                            <option value='' disabled selected>Vyberte šablonu</option>
                            {templateList.data?.items.map((template) => (
                                <option key={template.templateId} value={template.templateId}>{template.label}</option>    
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="documentType" className="block mb-1">Typ dokumentu</Label>
                        <Select
                            id="documentType"
                            {...register('documentType', { required: 'Typ dokumentu je povinný.' })}
                            color={errors.documentType ? 'failure' : 'default'}
                            disabled={!templateConfig}
                            helperText={(
                                <>
                                    {errors.documentType && (
                                        <span className='block'>
                                            {errors.documentType.message}
                                        </span>
                                    )}
                                    <span className='block font-semibold'>Pozor: po zaplacení dokumentu již nelze šablonu ani typ změnit.</span>
                                </>
                            )}
                        >
                            <option value='' disabled selected>Vyberte typ dokumentu</option>
                            {templateConfig?.types && sortedDocumentTypeKeys(templateConfig.types)
                                .map((type) => (
                                    <option key={type} value={type}>{templateConfig.types[type].label}</option>
                                ))}
                        </Select>
                    </div>

                    <div className='flex flex-col md:flex-row items-center gap-4'>
                        <span className='text-sm font-medium text-orange-600 text-center md:text-left'>Nenašli jste Vaši školu nebo fakultu? Napište nám a získejte slevu na korekturu.</span>
                        <Button
                            color='help'
                            className='flex-shrink-0'
                            onClick={handleMissingTemplate}
                        ><LifeBuoyIcon size={18} /> <span className='ml-2'>Chybí mi šablona</span></Button>
                    </div>

                    {templateConfig && templateTypeConfig && (
                        <div className='flex flex-col md:flex-row md:items-center justify-between gap-2 gap-y-4 text-sm p-4 border border-gray-300 rounded-md'>
                            <div className='flex flex-col gap-1'>
                                <span className='font-semibold'>Jazyk dokumentu</span>
                                <span className='font-medium'>{DocumentLanguageLabels[templateConfig.language] ?? templateConfig.language}</span>
                            </div>
                            <div className='flex flex-col gap-1'>
                                <span className='font-semibold'>Rozsah</span>
                                <span className='font-medium'>
                                    {templateTypeConfig.minPages}
                                    {templateTypeConfig.maxPages ? ` - ${templateTypeConfig.maxPages}` : ''} normostran
                                </span>
                            </div>
                            <div className='flex flex-col gap-1'>
                                <span className='font-semibold'>Citační norma</span>
                                <span className='font-medium'>{DocumentCitationStyleLabels[templateConfig.citationStyle] ?? templateConfig.citationStyle}</span>
                            </div>
                        </div>
                    )}

                    <div>
                        <Label htmlFor="goal" className="block mb-1">Cíl dokumentu</Label>
                        <Textarea
                            id="goal"
                            {...register('goal', {
                                maxLength: { value: 10000, message: 'Maximální délka je 10000 znaků' }
                            })}
                            placeholder='Cílem práce je popsat a analyzovat dopad X na Y...'
                            rows={6}
                            color={errors.goal ? 'failure' : 'default'}
                            helperText={(
                                <>
                                    <span className='block text-sm'>Popište v několika větách, o čem je Vaše práce. Toto pole můžete ponechat prázdné a doplnit jej později v nastavení dokumentu. Zadaný popis pomáhá naší AI lépe pochopit cíl Vaší práce.</span>
                                    {errors.goal && (
                                        <span className='block'>
                                            {errors.goal.message}
                                        </span>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    <Button
                        type='submit'
                        size='lg'
                        color='primary'
                        disabled={saveNewDocument.isPending}
                    >Vytvořit dokument</Button>

                    <ErrorSwitch
                        error={saveNewDocument.error}
                        default={{ message: 'Při ukládání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                        options={[]}
                    />
                </form>
            </div>
        </>
    );
};

export default NewDocumentView;
